/**
 * Composable for handling guest budget transfer after login
 */
export const useBudgetTransfer = () => {
  const { getSavedGuestBudget, clearGuestBudget } = useGuestMode()
  const { showError, showSuccess } = useErrorPopup()

  /**
   * Transfer guest budget to authenticated user account
   */
  const transferGuestBudget = async (userId: string) => {
    try {
      // Get saved guest budget from localStorage
      const guestBudget = getSavedGuestBudget()
      
      if (!guestBudget) {
        
        return { success: true, message: 'No guest budget to transfer' }
      }

      // Call the transfer API
      const { data } = await $fetch('/api/budget/transfer-guest', {
        method: 'POST',
        body: {
          guestBudget,
          userId
        }
      })

      if (data?.success) {
        // Clear the guest budget from localStorage after successful transfer
        clearGuestBudget()
        
        showSuccess(data.message || 'Your guest budget has been transferred to your account!')
        
        return {
          success: true,
          transferred: true,
          budget: data.budget
        }
      } else if (data?.conflictType === 'budget_exists') {
        // Budget already exists for this month/year
        showError(data.error || 'You already have a budget for this month.')
        
        // Clear the guest budget since we can't transfer it
        clearGuestBudget()
        
        return {
          success: false,
          conflict: true,
          error: data.error
        }
      } else {
        throw new Error(data?.error || 'Failed to transfer budget')
      }
    } catch (error: any) {
      console.error('Budget transfer error:', error)
      showError(error.message || 'Failed to transfer your guest budget')
      
      return {
        success: false,
        error: error.message || 'Transfer failed'
      }
    }
  }

  /**
   * Check if there's a guest budget that needs to be transferred
   */
  const hasGuestBudgetToTransfer = () => {
    if (process.client) {
      return getSavedGuestBudget() !== null
    }
    return false
  }

  return {
    transferGuestBudget,
    hasGuestBudgetToTransfer
  }
}
